import { useCallback } from 'react';
import { matchPath, useLocation } from 'react-router-dom';
import {
  useCartActionToastStore,
  type CartActionToastAction,
} from '@hungryroot/global-state';
import {
  BROWSE_ROUTE,
  EDIT_DELIVERY_GROCERIES_ROUTE,
  EDIT_DELIVERY_RECIPES_ROUTE,
  MY_HUNGRYROOT_ROUTE,
} from '@hungryroot/routes';
import { useIsMobilePopsicleEnabled } from './useIsMobilePopsicleEnabled';

/**
 * Surfaces that show the add/remove toast: the Exp 242 shopping surfaces where a
 * customer adds to cart — home, the whole Search/Browse subtree, and the
 * Edit-Delivery recipe + grocery flows. The toast is intentionally scoped to
 * these (e.g. not cart detail / smartcart) so the trigger no-ops elsewhere.
 *
 * `/browse/` covers every browse landing (groceries, meals, recipes, cookbooks)
 * and the `/edit-delivery/{recipes,groceries}/` prefixes cover their `/for-you/`
 * children. Matching only `/browse/groceries/` + `/edit-delivery/recipes/`
 * missed `/browse/meals/`, `/browse/recipes/`, and `/edit-delivery/groceries/`.
 */
const SHOP_SURFACE_ROUTES = [
  BROWSE_ROUTE,
  EDIT_DELIVERY_RECIPES_ROUTE,
  EDIT_DELIVERY_GROCERIES_ROUTE,
];

function isShopSurfacePath(pathname: string): boolean {
  const isHome =
    matchPath(pathname, {
      path: MY_HUNGRYROOT_ROUTE,
      exact: true,
      strict: false,
    }) !== null;
  const isSearchOrBrowse = SHOP_SURFACE_ROUTES.some(
    (path) =>
      matchPath(pathname, { path, exact: false, strict: false }) !== null
  );
  return isHome || isSearchOrBrowse;
}

/**
 * Returns a callback that surfaces the Exp 242 cart-action toast — but only on
 * mobile, in the Exp 242 variant, and on the shopping surfaces above. Everywhere
 * else it's a no-op, so the shared cart-action paths (`useRDPModalCart`,
 * `usePdpFooter`, `useShopActions`) can call it unconditionally on every
 * add/remove without leaking the toast onto other surfaces.
 */
export function useCartActionToastTrigger(): (
  action: CartActionToastAction
) => void {
  const { isEnabled: isMobilePopsicle } = useIsMobilePopsicleEnabled();
  const { pathname } = useLocation();
  const showCartActionToast = useCartActionToastStore(
    (state) => state.showCartActionToast
  );
  const enabled = isMobilePopsicle && isShopSurfacePath(pathname);

  return useCallback(
    (action: CartActionToastAction) => {
      if (enabled) {
        showCartActionToast(action);
      }
    },
    [enabled, showCartActionToast]
  );
}
